/**
 * useFleetSparklineData — batch-loads last 24h of telemetry for all org properties.
 * Calls `get_fleet_sparkline_data` RPC which aggregates from raw weekly-partitioned
 * telemetry server-side (NOT telemetry_hourly, which only has data >7 days old).
 *
 * Returns a Map<propertyId, SparklinePoint[]> keyed by property ID.
 * Points are sorted ascending by bucket time. Power values already in kW from the RPC.
 *
 * Uses same query cycle as useFleetSystems (staleTime: 90s, refetchInterval: 2min).
 */

import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { SparklinePoint } from '@/components/fleet/FleetSparkline';

/** Row shape returned by get_fleet_sparkline_data RPC. */
interface SparklineRpcRow {
  property_id: string;
  bucket_start: string;
  soc_percent: number | null;
  solar_kw: number | null;
  load_kw: number | null;
}

export function useFleetSparklineData(): {
  sparklineByPropertyId: Map<string, SparklinePoint[]>;
  isLoading: boolean;
} {
  const { properties, loading: appLoading, overrideOrgId, userProfile } = useApp();
  const orgId = overrideOrgId ?? userProfile?.org_id ?? null;

  const query = useQuery({
    queryKey: ['fleet-sparklines', orgId],
    queryFn: async (): Promise<Map<string, SparklinePoint[]>> => {
      if (!orgId || !properties?.length) return new Map();

      // Single RPC call — server-side aggregation, org-scoped, partition-pruned.
      // Returns ~24 rows per property (hourly buckets), already sorted by property + bucket.
      const { data: rows, error } = await supabase.rpc('get_fleet_sparkline_data', {
        p_org_id: orgId,
      });

      if (error) {
        console.error('Failed to load sparkline data:', error.message);
        return new Map();
      }

      const map = new Map<string, SparklinePoint[]>();
      for (const row of (rows ?? []) as SparklineRpcRow[]) {
        const points = map.get(row.property_id) ?? [];
        points.push({
          bucket: row.bucket_start,
          avgSoc: row.soc_percent,
          avgPvKw: row.solar_kw,   // Already in kW from RPC
          avgLoadKw: row.load_kw,   // Already in kW from RPC
        });
        map.set(row.property_id, points);
      }

      return map;
    },
    enabled: Boolean(orgId) && !appLoading,
    staleTime: 90 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  return {
    sparklineByPropertyId: query.data ?? new Map(),
    isLoading: query.isLoading,
  };
}
